import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import useScrollToBottom from '@sv-support-sdk/utils/src/hooks/useScrollToBottom';

import { $l } from '../../locales';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { ticketsSelector, selectTicketsLoading } from '../../store/selectors/tickets';
import ticketsActions from '../../store/actions/tickets';
import useMobileDetect from '../../hooks/useMobileDetect';

import Table from '../../components/Content/Table';
import ContentPlaceholder from '../ContentPlaceholder';
import TicketHistoryCards from './components/TicketHistoryCards';
import MobileLoader from '../../components/Feedback/Loader/MobileLoader';
import PageTabsContent from '../../components/Layout/Page/PageTabsContent';

import { routes } from '../../routing';
import modalActions from '../../store/actions/modals';
import { CONTACT_US_MODAL } from '../../constants/modals';
import { userAuthorizedSelector } from '../../store/selectors/user';
import { WORKFLOW_FRONT_URL } from '../../config';
import { ticketListColumns } from '../../tableColumns/ticket';

const TicketsHistory = () => {
  const dispatch = useAppDispatch();
  const tickets = useAppSelector(ticketsSelector);
  const ticketsLoading = useAppSelector(selectTicketsLoading);
  const navigate = useNavigate();
  const node = useRef<HTMLDivElement>(null);
  const isAuthorized = useAppSelector(userAuthorizedSelector);
  const { isBottom } = useScrollToBottom(node);
  const isMobile = useMobileDetect();

  const redirectToLogin = () => {
    window.location.href = `${WORKFLOW_FRONT_URL}/login?redirect=${window.location.href}`;
  };

  useEffect(() => {
    return () => {
      dispatch(ticketsActions.resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isAuthorized) {
      dispatch(ticketsActions.getTickets());
    }
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    if (isBottom) {
      dispatch(ticketsActions.getTickets({}, true));
    }
  }, [isBottom]);

  useEffect(() => {
    if (tickets.length && !ticketsLoading && node.current) {
      //@ts-ignore
      const isScrollable = node.current.scrollHeight > node.current.clientHeight;

      if (!isScrollable) {
        dispatch(ticketsActions.getTickets({}, true));
      }
    }
  }, [tickets, ticketsLoading, node]);

  const isNoTickets = !tickets.length && !ticketsLoading;
  const isAnyTickets = !!tickets.length && isAuthorized;

  return (
    <PageTabsContent ref={node}>
      {isNoTickets && (
        <ContentPlaceholder
          icon="96--exclamation-in-circle"
          title={$l('TICKETS.NO_TICKETS_YET')}
          description={
            isAuthorized ? $l('TICKETS.TICKET_HISTORY_INFO') : $l('TICKETS.LOGIN_TO_SEE_TICKETS')
          }
          action={{
            text: isAuthorized ? $l('TICKETS.CREATE_NEW_TICKET') : $l('HEADER.LOG_IN'),
            onClick: isAuthorized
              ? () => dispatch(modalActions.openModal({ name: CONTACT_US_MODAL }))
              : redirectToLogin,
          }}
        />
      )}
      {isAnyTickets && !isMobile && (
        <>
          <Table
            onRowClick={({ id }) => navigate(`${routes.tickets}/${id}`)}
            data={tickets}
            mapKey="id"
            columns={ticketListColumns}
          />
        </>
      )}
      {isAnyTickets && isMobile && <TicketHistoryCards tickets={tickets} />}
      {ticketsLoading && <MobileLoader />}
    </PageTabsContent>
  );
};

export default TicketsHistory;
